<template>
  <div>
    <calendar
      ref="calendar"
      :phone-number="searchObject.phoneNumber"
      :is-parent-loading="status.isLoading"
      :scheduled-orders="calendarScheduledOrders"
      @date-changed="onDateCalendarChanged"
      @choose-scheduled-delivery="getDetailedScheduledOrder"
    />
    <div
      :id="pageId"
      class="page-content"
      :class="pageId"
    >
      <h5 class="is-desktop">
        Here's the list of selected user's scheduled deliveries. You can cancel or edit them here.
      </h5>
      <div>
        <p-button
          v-if="searchObject.phoneNumber"
          type="purple"
          round
          @click="goToSearchPage()"
        >
          CREATE AN ORDER
        </p-button>
      </div>

      <editable-section
        title="Scheduled Deliveries"
        class="mt-20"
        :is-read-only="true"
      >
        <template #section-content>
          <div>
            <fieldset>
              <div class="row align-items-center">
                <label class="col-sm-3 control-label"> Phone Number* </label>
                <phone-number-input
                  id="customer-phone"
                  v-model="searchObject.phoneNumber"
                  class="col-sm-5"
                  :page-id="pageId"
                  :required="true"
                />
              </div>
            </fieldset>

            <fieldset>
              <div class="row align-items-center">
                <label class="col-sm-3 control-label">Scheduled Order ID*</label>
                <div class="col-sm-5">
                  <sanitized-input
                    id="scheduledDeliveryId"
                    v-model="searchObject.id"
                    placeholder="Please input scheduled-order id"
                  />
                </div>
              </div>
            </fieldset>

            <fieldset>
              <div class="row align-items-center">
                <label class="col-sm-3 control-label">Status</label>
                <div class="col-sm-8 filter-button-group mobile-pl-5 mobile-pr-0">
                  <p-button
                    type="info"
                    data-test="btn-active"
                    round
                    @click="searchObject.isActive = !searchObject.isActive"
                  >
                    <span
                      v-if="searchObject.isActive"
                      class="btn-label"
                    ><i class="fa fa-check" /></span>
                    Active
                  </p-button>

                  <p-button
                    type="info"
                    data-test="btn-finished"
                    round
                    @click="searchObject.isFinished = !searchObject.isFinished"
                  >
                    <span
                      v-if="searchObject.isFinished"
                      class="btn-label"
                    ><i class="fa fa-check" /></span>
                    Finished
                  </p-button>

                  <p-button
                    type="info"
                    data-test="btn-canceled"
                    round
                    @click="searchObject.isCanceled = !searchObject.isCanceled"
                  >
                    <span
                      v-if="searchObject.isCanceled"
                      class="btn-label"
                    ><i class="fa fa-check" /></span>
                    Canceled
                  </p-button>
                </div>
              </div>

              <div class="form-submit-button-group mb-20">
                <p-button
                  type="purple"
                  native-type="submit"
                  round
                  :disable="!searchObject.id && !searchObject.phoneNumber"
                  @click="loadScheduledDeliveries"
                >
                  Get Scheduled Deliveries
                </p-button>
                <p-button
                  type="yellow"
                  round
                  :disabled="status.isLoading"
                  @click="resetForm"
                >
                  Reset
                </p-button>
              </div>
            </fieldset>
          </div>
          <hr>
          <fieldset>
            <div class="row align-items-center">
              <pagination
                ref="pagination"
                :is-loading="status.isLoading"
                :total-pages="status.totalPages"
                @fetch-data="getTableScheduledDeliveries"
              />
            </div>
          </fieldset>

          <message-html :result-html="status.resultHtml" />
          <table-view-container
            class="pt-10 pb-10 pl-10 pr-10"
            :page-id="pageId"
            :data="scheduledOrders"
            :table-columns="tableColumns"
            :fixed-column-counts="3"
          >
            <template #custom-columns>
              <el-table-column
                v-if="scheduledOrders.length > 0"
                label="Actions"
              >
                <template #default="scope">
                  <el-button
                    size="small"
                    @click="getDetailedScheduledOrder(scope.row.id)"
                  >
                    Detail
                  </el-button>
                </template>
              </el-table-column>
            </template>
          </table-view-container>
        </template>
      </editable-section>
    </div>

    <delivery-scheduler
      v-if="detailedOrder"
      v-model:show="status.isScheduledDeliveryOpen"
      :status="detailedOrder.orderStatus.title"
      :scheduled-order-id="detailedOrder.id"
      :estimate="detailedOrder.storeOrder.estimate"
      :store-title="detailedOrder.storeOrder.title"
      :customer-info="detailedOrder.caller"
      :initial-schedule="detailedOrder.schedule"
      @update-delivery="onUpdateDelivery"
      @cancel-delivery="onCancelDelivery"
      @edit-order="goToSearchPage"
    />
  </div>
</template>

<script lang="ts">
import { ref, Ref, reactive, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElLoading } from 'element-plus';
import _ from 'lodash';
import moment from 'moment-timezone';
import queryString from 'query-string';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { TableColumnType } from '@/models/table';
import { scheduledDeliveryService } from '@/services/scheduledDelivery';

import Calendar from './ScheduledDelivery/Calendar.vue';
import EditableSection from '@/containers/EditableSection.vue';
import DeliveryScheduler from '@/components/Main/Operator/ScheduledDelivery/DeliveryScheduler.vue';

type SearchCondition = {
  id?: string;
  phoneNumber?: string;
  isActive?: boolean;
  isFinished?: boolean;
  isCanceled?: boolean;
  dates?: { startDate: string; endDate: string };
};

const pageId = 'scheduled-deliveries-list';
const paginationLimit = 10;

const tableColumns: TableColumnType[] = [
  { label: 'No', field: 'index', width: 60, isCenterAlign: true },
  { label: 'Scheduled Order ID', field: 'id', width: 150 },
  { label: 'Caller Id', field: 'callerId', width: 180 },
  { label: 'Store Name', field: 'storeOrder', width: 180, formatter: (storeOrder: any) => storeOrder?.title },
  {
    label: 'Estimate',
    field: 'storeOrder',
    formatter: (storeOrder: any) => {
      return `${_.get(storeOrder, 'estimate.timeEstimate.min')} - ${_.get(storeOrder, 'estimate.timeEstimate.max')} min`;
    },
    width: 180,
  },
  {
    label: 'Address',
    field: 'storeOrder',
    width: 180,
    formatter: (storeOrder: any) => _.get(storeOrder, 'estimate.address.formattedAddress'),
  },
  {
    label: 'Total',
    field: 'total',
    formatter: (total: number) => {
      return total && `$${(total / 100).toFixed(2)}`;
    },
    width: 180,
  },
  { label: 'CreatedAt', field: 'createdAt', width: 180 },
];

export default {
  name: 'ScheduledDelivery',

  components: {
    DeliveryScheduler,
    EditableSection,
    Calendar,
  },

  setup: () => {
    const route = useRoute();
    const router = useRouter();

    const { customerFullName, customerPhoneNumber } = useCustomer();
    const { showSuccessPopupDialog, showErrorPopupDialog, showConfirmPopupDialog, showConfirmPopupDialogWithValidation } = useSwalDialog();

    const scheduledOrders: Ref<any> = ref([]);
    const calendarScheduledOrders: Ref<any> = ref([]);
    const calendarDates: Ref<{ startDate: string; endDate: string } | undefined> = ref(undefined);
    const detailedOrder: Ref<any> = ref(null);
    const searchObject = reactive({
      phoneNumber: '',
      id: '',
      isActive: true,
      isFinished: true,
      isCanceled: true,
    });
    const status = reactive({
      resultHtml: '',
      isLoading: false,
      resultHistoryHTML: '',
      isLoadingHistory: false,
      totalPages: 1,
      currentPage: 1,
      isScheduledDeliveryOpen: false,
    });

    const getScheduledDelivery = async (currentPage: number, searchCondition: SearchCondition, isPaginationEnabled: boolean = true) => {
      status.resultHtml = '';

      const loading = ElLoading.service({
        lock: true,
        text: 'Loading Scheduled Deliveries...',
        background: 'rgba(0, 0, 0, 0.7)',
        customClass: 'custom-full-spinner-class',
      });

      status.isLoading = true;

      try {
        const { isActive, isFinished, isCanceled, phoneNumber: searchPhoneNumber, id, dates } = searchCondition;

        const pagination = {
          limit: paginationLimit,
          offset: (currentPage - 1) * paginationLimit,
        };

        const where = {
          isActive,
          isFinished,
          isCanceled,
          callerPhone: util.getValidPhoneNumber(searchPhoneNumber || '') || null,
        } as any;

        if (id) {
          where.whereSchedule = {
            id,
          };
        }

        const response = await scheduledDeliveryService.getGourmetScheduledOrders({
          where,
          pagination: isPaginationEnabled ? pagination : undefined,
          startDate: _.get(dates, 'startDate'),
          endDate: _.get(dates, 'endDate'),
        });

        return response.rows;
      } catch (error) {
        scheduledOrders.value = [];
        status.totalPages = 1;
        status.resultHtml = util.getGraphqlError(error);
        return [];
      } finally {
        loading.close();
        status.isLoading = false;
      }
    };

    const getTableScheduledDeliveries = async (page: number) => {
      if (!searchObject.phoneNumber && !searchObject.id) {
        return;
      }

      status.currentPage = page;

      const deliveries = await getScheduledDelivery(status.currentPage, searchObject);

      status.totalPages = Math.ceil(deliveries.length / paginationLimit) || 1;

      scheduledOrders.value = deliveries.map((scheduleOrder: any, index: number) => {
        const order = _.cloneDeep(scheduleOrder);
        order.index = (status.currentPage - 1) * paginationLimit + index + 1;

        return order;
      });
    };

    const getCalendarScheduledDeliveries = async () => {
      if (!searchObject.phoneNumber || !calendarDates.value) {
        return;
      }

      calendarScheduledOrders.value = [];

      try {
        calendarScheduledOrders.value = await scheduledDeliveryService.getScheduledOrderCalendarRecords({
          phoneNumber: searchObject.phoneNumber,
          startDate: calendarDates.value.startDate,
          endDate: calendarDates.value.endDate,
        });
      } catch (err) {
        console.log(err);
      }
    };

    const onDateCalendarChanged = async (dates: { startDate: string; endDate: string }) => {
      calendarDates.value = dates;

      await getCalendarScheduledDeliveries();
    };

    const getDetailedScheduledOrder = async (deliveryOrderId: any) => {
      try {
        const response = await scheduledDeliveryService.getGourmetScheduledOrders({
          where: {
            id: deliveryOrderId,
          },
          isDetail: true,
        });

        if (response.count === 0) {
          showErrorPopupDialog({
            text: 'Detailed order not found',
          });

          return;
        }

        detailedOrder.value = response.rows[0];
        const schedule = detailedOrder.value.schedule;

        const deliveryDateTimeLocal = moment
          .utc(`${schedule.deliveryDate} ${schedule.deliveryTime}`, 'YYYY-MM-DD HH:mm')
          .tz(schedule.timeZone);

        schedule.deliveryDate = deliveryDateTimeLocal.format('YYYY-MM-DD');
        schedule.deliveryTime = deliveryDateTimeLocal.format('HH:mm');

        const endOnLocal =
          detailedOrder.value.endOn && moment.utc(`${schedule.endOn} ${schedule.deliveryTime}`, 'YYYY-MM-DD HH:mm').tz(schedule.timeZone);

        if (endOnLocal) {
          schedule.endOn = endOnLocal.format('YYYY-MM-DD');
        }

        if (schedule.skipDates) {
          schedule.skipDates = schedule.skipDates.map((date: any) => moment(date).toDate());
        }

        status.isScheduledDeliveryOpen = true;
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      }
    };

    const onUpdateDelivery = async (schedule: any) => {
      const questionText = `Update Schedule for account: ${customerFullName.value} (${customerPhoneNumber.value})?`;
      const isUpdateDelivery = await showConfirmPopupDialog({
        text: questionText,
        title: 'Update Schedule?',
        confirmButtonText: 'Update',
      });

      if (!isUpdateDelivery) {
        return;
      }

      status.isLoading = true;
      try {
        await scheduledDeliveryService.updateScheduledDelivery(schedule);

        showSuccessPopupDialog({
          text: `The schedule has been successfully updated for the account: ${customerFullName.value} (${customerPhoneNumber.value})`,
        });

        await loadScheduledDeliveries();
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      } finally {
        status.isScheduledDeliveryOpen = false;
        status.isLoading = false;
      }
    };

    const onCancelDelivery = async (orderId: string) => {
      const reason = await showConfirmPopupDialogWithValidation({
        text: `Cancel Scheduled Delivery ${orderId}?`,
        title: `Cancel Delivery?`,
        input: 'textarea',
        inputPlaceholder: 'Please input reason',
        validationText: `Please provide the reason for cancel scheduled delivery`,
        confirmButtonText: 'Cancel',
      });

      if (!reason) {
        return;
      }

      status.isLoading = true;
      try {
        await scheduledDeliveryService.cancelScheduledDelivery({
          deliveryOrderId: orderId,
          reason,
        });

        showSuccessPopupDialog({
          text: 'The scheduled delivery has been canceled successfully.',
        });
        await loadScheduledDeliveries();
        status.isScheduledDeliveryOpen = false;
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      }
      status.isLoading = false;
    };

    const loadScheduledDeliveries = async () => {
      await getTableScheduledDeliveries(1);
      await getCalendarScheduledDeliveries();
    };

    const goToSearchPage = (deliveryId?: number) => {
      const queryParams = queryString.stringify({
        phoneNumber: searchObject.phoneNumber,
      }) as string;

      if (deliveryId) {
        router.push(`/operator/delivery-search/${deliveryId}?${queryParams}`);
      } else {
        router.push(`/operator/delivery-search?${queryParams}`);
      }
    };

    const resetForm = () => {
      status.resultHtml = '';
      searchObject.isActive = true;
      searchObject.isFinished = true;
      searchObject.isCanceled = true;
      searchObject.phoneNumber = '';
      searchObject.id = '';

      scheduledOrders.value = [];
      calendarScheduledOrders.value = [];
    };

    watch(customerPhoneNumber, async (value: string) => {
      resetForm();

      if (searchObject.phoneNumber !== value) {
        searchObject.phoneNumber = value;
        await loadScheduledDeliveries();
      }
    });

    onMounted(async () => {
      util.moveToTop();
      searchObject.phoneNumber = customerPhoneNumber.value;
      searchObject.id = (route.query.scheduledOrderId as string) || '';

      if (searchObject.phoneNumber || searchObject.id) {
        loadScheduledDeliveries();
      }
    });

    return {
      pageId,
      scheduledOrders,
      calendarScheduledOrders,
      tableColumns,
      detailedOrder,
      searchObject,
      status,
      util,

      loadScheduledDeliveries,
      getTableScheduledDeliveries,
      getDetailedScheduledOrder,
      onDateCalendarChanged,
      goToSearchPage,
      resetForm,
      onUpdateDelivery,
      onCancelDelivery,
    };
  },
};
</script>

<style lang="scss">
.scheduled-deliveries-list {
  padding-bottom: 20px;
  .btn {
    padding: 9px 15px;
  }
  .form-submit-button-group {
    position: relative;
    @media (max-width: 576px) {
      button {
        margin-bottom: 5px !important;
      }
    }
    .display-order {
      position: absolute;
      right: 20px;
      @media (max-width: 576px) {
        position: relative;
        right: 0;
      }
    }
  }
}
.search-button {
  text-align: right;
}
</style>
